// ============================================================================
// LE CHAMP DE FORMULAIRE — étiquette, saisie, aide, erreur.
//
// ⚠️ CE QU'IL TIENT, ET QUI SE PERD TOUJOURS QUAND CHAQUE ÉCRAN LE RÉÉCRIT :
//   · l'étiquette est LIÉE au champ (`htmlFor`) — sinon cliquer dessus ne fait rien, et le lecteur
//     d'écran annonce « zone de saisie » sans dire de quoi ;
//   · l'aide et l'erreur sont RATTACHÉES par `aria-describedby` — écrites à côté sans lien, elles
//     n'existent que pour qui les voit ;
//   · une erreur porte `aria-invalid` ET une couleur ET un texte : la couleur seule ne survit pas au
//     daltonisme, et l'attribut seul ne se voit pas.
//
// ⚠️ UN CHAMP NON MODIFIABLE N'EST PAS UN CHAMP (règle relevée dans la maquette : zéro `disabled`
// dans ses 23 000 lignes). On rend une VALEUR habillée — jamais une saisie inerte, qui prend le
// focus pour ne rien accepter.
// ============================================================================
import { useId } from 'react';
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import {
  FIELD_GROUP, FIELD_HINT, FIELD_LABEL, FIELD_READONLY, FIELD_SIZES, type FieldSize,
} from '@orgazone/ui-tokens';
import { Icon } from './icons';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  size?: FieldSize;
  /** Densité de l'étiquette : `form` en fiche et wizard, `dense` en modale et réglages. */
  labelStyle?: 'form' | 'dense';
  /** ⚠️ Écrit EN TOUTES LETTRES, jamais un astérisque : une convention que personne n'a apprise ne se lit pas. */
  required?: boolean;
  hint?: string;
  /** Message d'erreur. Présent ⇒ le champ est invalide, et il le DIT de trois façons. */
  error?: string;
  /** « Modifié, pas encore enregistré » — une attente, pas une faute. */
  pending?: boolean;
}

export function Field({
  label, size = 'field', labelStyle = 'form', required, hint, error, pending, style, ...input
}: FieldProps) {
  const id = useId();
  const hintId = `${id}-aide`;
  const errId = `${id}-erreur`;
  const cran = FIELD_SIZES[size];
  const lab = FIELD_LABEL[labelStyle];

  // ⚠️ SEULE LA GÉOMÉTRIE EST EN LIGNE. Surface, bordure, survol et focus vivent dans la charte
  // (`.oz-field`) : un `border` en style en ligne bat toute règle de feuille, donc un champ qui
  // porterait sa bordure ici n'aurait jamais ni survol ni anneau de focus, quoi qu'on écrive ailleurs.
  const champ: CSSProperties = {
    height: cran.height, padding: `0 ${cran.padH}px`,
    borderRadius: `var(--r-${cran.radius})`,
    fontSize: cran.fontSize,
    ...style,
  };

  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block', fontSize: lab.fontSize, fontWeight: lab.weight,
          color: error ? `var(--${FIELD_LABEL.errorInk})` : `var(--${lab.ink})`,
          marginBottom: lab.marginBottom,
        }}
      >
        {label}
        {required && FIELD_LABEL.requiredSuffix}
      </label>
      <input
        id={id}
        className="oz-field"
        data-pending={pending ? '' : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={[error ? errId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined}
        style={champ}
        {...input}
      />
      {/* ⚠️ SOUS le champ, jamais en infobulle : une aide qu'il faut survoler n'aide pas — et elle
          n'existe pas du tout au clavier. */}
      {hint && !error && (
        <p id={hintId} style={{ margin: `${FIELD_HINT.marginTop}px 0 0`, fontSize: FIELD_HINT.fontSize, color: `var(--${FIELD_HINT.ink})`, lineHeight: FIELD_HINT.lineHeight }}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} role="alert" style={{ margin: `${FIELD_HINT.marginTop}px 0 0`, fontSize: FIELD_HINT.fontSize, fontWeight: FIELD_HINT.errorWeight, color: `var(--${FIELD_HINT.errorInk})`, lineHeight: FIELD_HINT.lineHeight }}>
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * UNE VALEUR QU'ON NE MODIFIE PAS — calculée, ou verrouillée.
 * ⚠️ CE N'EST PAS UN `<input disabled>` : un champ désactivé prend le focus pour ne rien accepter, et
 * son texte grisé devient illisible. On rend la valeur telle qu'elle est, dans une boîte qui dit
 * qu'elle ne se saisit pas.
 */
export function ReadOnlyValue({ label, value, locked, hint }: {
  label: string;
  value: ReactNode;
  /** Verrouillé par une règle (contrat, droit) plutôt que simplement calculé. */
  locked?: boolean;
  hint?: string;
}) {
  const style = locked ? FIELD_READONLY.locked : FIELD_READONLY.computed;
  const cran = FIELD_SIZES.field;
  // ⚠️ LE LIBELLÉ DÉSIGNE LA VALEUR : `<output>` est un élément étiquetable, et sans le lien une aide technique la lit
  //    sans nom.
  const id = useId();
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', fontSize: FIELD_LABEL.form.fontSize, fontWeight: FIELD_LABEL.form.weight, color: `var(--${FIELD_LABEL.form.ink})`, marginBottom: FIELD_LABEL.form.marginBottom }}>
        {label}
      </label>
      <output
        id={id}
        style={{
          display: 'flex', alignItems: 'center', gap: 7, width: '100%',
          height: cran.height, padding: `0 ${cran.padH}px`,
          borderRadius: `var(--r-${cran.radius})`,
          border: `1px solid var(--${style.border})`, background: `var(--${style.bg})`,
          color: 'var(--ink-900)', fontSize: cran.fontSize, fontWeight: style.weight,
        }}
      >
        {locked && <Icon name="lock" size={FIELD_READONLY.locked.lockIcon} color="var(--ink-400)" />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
      </output>
      {hint && (
        <p style={{ margin: `${FIELD_HINT.marginTop}px 0 0`, fontSize: FIELD_HINT.fontSize, color: `var(--${FIELD_HINT.ink})`, lineHeight: FIELD_HINT.lineHeight }}>
          {hint}
        </p>
      )}
    </div>
  );
}

/** Un groupe de champs : un titre de section, et l'air qui l'entoure. */
export function FieldGroup({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section>
      {title && (
        <h3 style={{ margin: `0 0 ${FIELD_GROUP.titleMarginBottom}px`, fontSize: FIELD_GROUP.titleSize, fontWeight: FIELD_GROUP.titleWeight, color: `var(--${FIELD_GROUP.titleInk})` }}>
          {title}
        </h3>
      )}
      {/* ⚠️ La grille est INTÉRIEURE au groupe : l'écart entre champs ne doit pas s'appliquer aussi
          entre le titre et le premier champ — deux respirations différentes, deux conteneurs. */}
      <div style={{ display: 'grid', gap: FIELD_GROUP.gap }}>{children}</div>
    </section>
  );
}
